/**
 * @file Shared configuration for the token-refresh subsystem.
 *
 * Centralises magic strings, timing values, and option types so that
 * `core.ts`, `events.ts`, and `axios-interceptor.ts` all draw from a single
 * source of truth.
 *
 * Lives in `libs/utils` — below `libs/services` in the dependency graph — so
 * it cannot use `ConfigurationService`. Values here are infrastructure-level
 * constants that don't vary per-brand or per-environment.
 * {@link ITokenRefreshEventsOptions} provides an override mechanism at the call
 * site for anything that does need to vary.
 */

// ---------------------------------------------------------------------------
// Event listener options (type)
// ---------------------------------------------------------------------------

/**
 * Configuration for {@link import('./events').startTokenRefreshEvents}.
 */
export interface ITokenRefreshEventsOptions {
  /**
   * Browser events that trigger a refresh check.
   * @default ['visibilitychange', 'pageshow', 'online']
   *
   * @example
   * // Only refresh on visibility change:
   * { events: ['visibilitychange'] }
   */
  events?: Array<string>;

  /**
   * Minimum milliseconds between refresh attempts. Prevents rapid event
   * sequences (e.g. switching tabs quickly) from spamming the endpoint.
   * @default 5000
   */
  debounceMs?: number;

  /**
   * Server endpoint to POST to when a refresh is needed.
   * @default '/api/auth/refresh-tokens'
   */
  refreshUrl?: string;
}

// ---------------------------------------------------------------------------
// Config object
// ---------------------------------------------------------------------------

export const TOKEN_REFRESH_CONFIG = {
  /** Matches `<prefix>refresh_at=<epoch_seconds>` in `document.cookie`. */
  cookieRegex: /refresh_at=(\d+)/,

  /** Session-storage key that guards against infinite page-reload loops. */
  reloadFlagKey: '__session_refresh_reload',

  /** LocalStorage key for the cross-tab lock. Value is an epoch-ms expiry. */
  lockKey: '__token_refresh_lock',

  /**
   * How long a tab can hold the cross-tab lock before it auto-expires (ms).
   *
   * Set to 10 s to accommodate slow connections — typical SLAS refresh latency
   * is 200–800 ms, but mobile or congested networks can push this to several
   * seconds. The TTL must be long enough that a legitimate refresh completes
   * before the lock expires, yet short enough that a crashed tab doesn't block
   * other tabs for too long.
   */
  lockTtlMs: 10_000,

  /**
   * Polling interval while waiting for another tab's refresh to complete (ms).
   *
   * 500 ms balances responsiveness against CPU/battery cost. On a slow network
   * a waiting tab may poll ~20 times, which is negligible overhead.
   */
  lockPollMs: 500,

  /** Seconds before the `refresh_at` cookie to schedule the proactive timer. */
  timerBufferSeconds: 60,

  /** Default server endpoint to POST to when a refresh is needed. */
  defaultRefreshUrl: '/api/auth/refresh-tokens',

  /** Default values for event-listener options. */
  eventDefaults: {
    events: ['visibilitychange', 'pageshow', 'online'],
    debounceMs: 5_000,
    refreshUrl: '/api/auth/refresh-tokens'
  } satisfies Required<ITokenRefreshEventsOptions>
} as const;
